import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { CloseBold } from '@ha/icons';
import { useIntl } from 'ha/i18n';
import dayjs from 'dayjs';

import { AvailabilityFilterValue, dateRangeToAvailability } from '@ha/date';
import {
  SwipeableDrawer,
  IconButton,
  Divider,
  Grid,
  Button,
  units,
  Typography,
  Badge,
  Kind,
} from '@hbf/dsl';

import { SearchMoveInSelector } from './SearchMoveInSelector';
import { AvailabilityDuration } from './AvailabilityDuration';
import { EmptyState, SearchFiltersContext } from '../../SearchFiltersContext';

const useStyles = makeStyles()(theme => ({
  badge: {
    marginLeft: theme.spacing(1),
  },
  drawer: {
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    margin: 0,
  },
  drawerContent: {
    padding: units.rem(1),
  },
  closeIcon: {
    fontSize: units.rem(1),
  },
  title: {
    paddingTop: units.rem(1),
    paddingLeft: units.rem(1),
    paddingRight: units.rem(1),
  },
  container: {
    paddingLeft: units.rem(1),
    paddingRight: units.rem(1),
    paddingBottom: units.rem(1),
  },
  content: {
    marginBottom: units.rem(1.5),
  },
}));

interface AvailabilityFilterProps {
  onClear: () => void;
  onChange: (value?: AvailabilityFilterValue) => void;
  value?: AvailabilityFilterValue;
}

const AvailabilityFilter: React.FC<AvailabilityFilterProps> = ({
  onClear,
  onChange,
  value,
}) => {
  const [moveIn, setMoveIn] = useState(value?.moveIn.value);
  const [duration, setDuration] = useState(value?.duration);

  useEffect(() => {
    if (!moveIn) {
      onChange(undefined);
      return;
    }
    onChange({
      moveIn: {
        type: moveIn?.endsWith(dayjs(moveIn).daysInMonth().toString())
          ? 'month'
          : 'date',
        value: moveIn,
      },
      duration,
    });
  }, [duration, moveIn]);

  return (
    <React.Fragment>
      <SearchMoveInSelector
        onChange={setMoveIn}
        onClear={() => {
          setMoveIn(undefined);
          onClear();
        }}
        value={moveIn}
      />

      <AvailabilityDuration onChange={setDuration} duration={duration} />
    </React.Fragment>
  );
};

export const AvailabilityFilterButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { classes } = useStyles();
  const { T } = useIntl();

  const [badgeCount, setBadgeCount] = useState(0);
  const {
    localState: { dates },
  } = React.useContext(SearchFiltersContext);

  const availabilityValues = useMemo(
    () =>
      dates.startDate !== null
        ? dateRangeToAvailability({
            startDate: dates.startDate.toString(),
            endDate: dates.endDate?.toString(),
          })
        : undefined,
    [dates],
  );

  const { onChangeAvailability, onChangeDates } =
    React.useContext(SearchFiltersContext);

  const handleDatesClear = React.useCallback(() => {
    onChangeDates(EmptyState.dates);
  }, [onChangeDates]);

  const handleAvailabilityChange = (availability?: AvailabilityFilterValue) => {
    if (!availability) {
      handleDatesClear();
      return;
    }

    onChangeAvailability({ ...availabilityValues, ...availability });
  };

  useEffect(() => {
    setBadgeCount(
      [availabilityValues?.moveIn?.value, availabilityValues?.duration].filter(
        value => !!value,
      ).length,
    );
  }, [availabilityValues]);

  const handleClose = useCallback(() => setOpen(false), []);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleApply = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <React.Fragment>
      <Button
        variant={badgeCount > 0 ? 'contained' : 'outlined'}
        color="primary"
        size="small"
        onClick={handleOpen}
      >
        {badgeCount === 0 ? (
          T('Availability')
        ) : (
          <Badge
            kind={Kind.inline}
            color="white"
            badgeContent={badgeCount}
            badgeClassName={classes.badge}
          >
            {T('Availability')}
          </Badge>
        )}
      </Button>
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        PaperProps={{ className: classes.drawer }}
      >
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          className={classes.title}
        >
          <Typography.ParagraphTitle>
            {T('Availability')}
          </Typography.ParagraphTitle>
          <IconButton size="small" onClick={handleClose}>
            <CloseBold className={classes.closeIcon} />
          </IconButton>
        </Grid>

        <Divider kind="spaced-m" />

        <Grid
          container
          direction="column"
          alignItems="stretch"
          className={classes.container}
        >
          <Grid item className={classes.content}>
            <AvailabilityFilter
              onChange={handleAvailabilityChange}
              value={availabilityValues}
              onClear={handleDatesClear}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleApply}
            >
              {T('Apply')}
            </Button>
          </Grid>
        </Grid>
      </SwipeableDrawer>
    </React.Fragment>
  );
};
